import type { ReactNode } from 'react'
import cx from 'classnames'
import {
  Card,
  CardContent,
  CardHeader,
  CardTabs,
  CardTitle,
} from '@/components/Card'
import { TabBar, TabItem } from '@/components/TabBar'
import { Button } from '@/components/Button'
import type { Id } from '@/infra/id'
import styles from './BookkeepingTrackerCard.module.css'
import type { BookkeepingTrackerCardTab } from '@/features/jobs/details/components/BookkeepingProgressionCard/BookkeepingProgressionViewModel'

export interface BookkeepingTrackerCardProps {
  activeTab: BookkeepingTrackerCardTab
  onTabChange: (tab: BookkeepingTrackerCardTab) => unknown
  children?: ReactNode
}

export function BookkeepingTrackerCard({
  activeTab,
  onTabChange,
  children,
}: BookkeepingTrackerCardProps) {
  return (
    <Card>
      <CardHeader compact>
        <CardTitle cardLayout>Bookkeeping tracker</CardTitle>
      </CardHeader>
      <CardTabs>
        <TabBar>
          <TabItem
            cardLayout
            active={activeTab === 'FinishedItems'}
            onPress={() => onTabChange('FinishedItems')}
          >
            Finished items
          </TabItem>
          <TabItem
            cardLayout
            active={activeTab === 'Cycles'}
            onPress={() => onTabChange('Cycles')}
          >
            Cycles
          </TabItem>
        </TabBar>
      </CardTabs>
      <CardContent scroll>{children}</CardContent>
    </Card>
  )
}

export interface BookkeepingFinishedItemsCardProps {
  children?: ReactNode
}

export function BookkeepingFinishedItemsCard({
  children,
}: BookkeepingFinishedItemsCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Finished items</CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  )
}

export interface BookkeepingFinishedItem {
  id: Id
  name: string
  completedCount: number
  quantity: number
}

export interface BookkeepingFinishedItemsListProps {
  items?: BookkeepingFinishedItem[]
  topPadding?: boolean
}

export function BookkeepingFinishedItemsList({
  items,
  topPadding,
}: BookkeepingFinishedItemsListProps) {
  return (
    <div
      className={cx(styles.finishedItemsList, topPadding && styles.topPadding)}
    >
      {items?.map((item) => (
        <div key={item.id} className={styles.finishedItem}>
          <div className={styles.finishedItemName}>{item.name}</div>
          <div className={styles.finishedItemCount}>
            {item.completedCount} of {item.quantity} completed
          </div>
        </div>
      ))}
    </div>
  )
}

export interface BookkeepingCyclesListProps {
  children?: ReactNode
}

export function BookkeepingCyclesList({
  children,
}: BookkeepingCyclesListProps) {
  return <div className={styles.cyclesItemsList}>{children}</div>
}

export interface BookkeepingCycleItemProps {
  statusName: string
  number: number
  date: string
  onView: () => unknown
}

export function BookkeepingCycleItem({
  statusName,
  number,
  date,
  onView,
}: BookkeepingCycleItemProps) {
  return (
    <div className={styles.cycleItem}>
      <div className={styles.cycleStatus}>
        {statusName} &bull; Cycle {number}
      </div>
      <div className={styles.cycleDateContainer}>
        <div className={styles.cycleDate}>{date}</div>
        <Button label="View" variant="text" size="baseline" onPress={onView} />
      </div>
    </div>
  )
}
